import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { CheckCircle, Package, Loader2, XCircle, ArrowRight } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { Button } from '../components/ui/button';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CheckoutSuccess = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { clearCart } = useCart();
  
  const [status, setStatus] = useState('loading');
  const [paymentInfo, setPaymentInfo] = useState(null);
  const [pollCount, setPollCount] = useState(0);
  
  const sessionId = searchParams.get('session_id');
  const maxPolls = 10;

  useEffect(() => {
    if (!sessionId) {
      navigate('/');
      return;
    }

    pollPaymentStatus();
  }, [sessionId]);

  const pollPaymentStatus = async () => {
    if (pollCount >= maxPolls) {
      setStatus('timeout');
      return;
    }

    try {
      const response = await axios.get(`${API}/checkout/status/${sessionId}`);
      const data = response.data;
      
      if (data.payment_status === 'paid') {
        setStatus('success');
        setPaymentInfo(data);
        clearCart();
        return;
      } else if (data.status === 'expired') {
        setStatus('expired');
        return;
      }
      
      // Continue polling
      setPollCount(prev => prev + 1);
      setTimeout(pollPaymentStatus, 2000);
    } catch (error) {
      console.error('Error checking payment status:', error);
      setStatus('error');
    }
  };

  return (
    <div className="min-h-screen section-cream flex items-center justify-center" data-testid="checkout-success-page">
      <div className="container-custom py-16">
        <div className="max-w-lg mx-auto text-center">
          {status === 'loading' && (
            <div data-testid="payment-loading">
              <Loader2 className="w-16 h-16 text-accent mx-auto mb-6 animate-spin" />
              <h1 className="font-serif text-2xl md:text-3xl text-text-primary mb-4">
                Vérification du paiement...
              </h1>
              <p className="text-text-secondary">
                Veuillez patienter pendant que nous confirmons votre paiement.
              </p>
            </div>
          )}

          {status === 'success' && (
            <div data-testid="payment-success">
              <div className="w-20 h-20 rounded-full bg-success/10 flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-10 h-10 text-success" />
              </div>
              <h1 className="font-serif text-2xl md:text-3xl text-text-primary mb-4">
                Merci pour votre commande !
              </h1>
              <p className="text-text-secondary mb-8">
                Votre paiement a été confirmé. Vous recevrez bientôt un email de confirmation.
              </p>
              
              {paymentInfo && (
                <div className="bg-white p-6 mb-8 text-left shadow-rose">
                  <h3 className="font-serif text-lg text-text-primary mb-4">
                    Récapitulatif
                  </h3>
                  <div className="flex justify-between text-text-secondary mb-2">
                    <span>Montant payé</span>
                    <span className="font-medium text-text-primary">
                      {paymentInfo.amount?.toFixed(2)} {paymentInfo.currency?.toUpperCase()}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 text-text-secondary mt-4 pt-4 border-t border-border">
                    <Package className="w-4 h-4 text-accent" />
                    <span className="text-sm">Livraison estimée sous 3-5 jours ouvrés</span>
                  </div>
                </div>
              )}

              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/products">
                  <Button className="btn-primary flex items-center gap-2">
                    Continuer mes achats
                    <ArrowRight className="w-4 h-4" />
                  </Button>
                </Link>
                <Link to="/">
                  <Button variant="outline" className="btn-outline">
                    Retour à l'accueil
                  </Button>
                </Link>
              </div>
            </div>
          )}

          {status === 'expired' && (
            <div data-testid="payment-expired">
              <div className="w-20 h-20 rounded-full bg-error/10 flex items-center justify-center mx-auto mb-6">
                <XCircle className="w-10 h-10 text-error" />
              </div>
              <h1 className="font-serif text-2xl md:text-3xl text-text-primary mb-4">
                Session expirée
              </h1>
              <p className="text-text-secondary mb-8">
                Votre session de paiement a expiré. Veuillez réessayer.
              </p>
              <Link to="/products">
                <Button className="btn-primary">
                  Retourner à la boutique
                </Button>
              </Link>
            </div>
          )}

          {(status === 'error' || status === 'timeout') && (
            <div data-testid="payment-error">
              <div className="w-20 h-20 rounded-full bg-error/10 flex items-center justify-center mx-auto mb-6">
                <XCircle className="w-10 h-10 text-error" />
              </div>
              <h1 className="font-serif text-2xl md:text-3xl text-text-primary mb-4">
                Erreur de vérification
              </h1>
              <p className="text-text-secondary mb-8">
                Nous n'avons pas pu vérifier votre paiement. Si vous avez été débité, 
                veuillez nous contacter.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/#contact">
                  <Button className="btn-primary">
                    Nous contacter
                  </Button>
                </Link>
                <Link to="/">
                  <Button variant="outline" className="btn-outline">
                    Retour à l'accueil
                  </Button>
                </Link>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CheckoutSuccess;
